/**
 * AuditEventGroup — one audit event block, shared by the rail card and modal
 * (BON-201 redesign). Layout: bold event title + actor·date·clock right-aligned
 * muted; then per-field sub-rows with small-caps FIELD label + before→after.
 */
import type { AuditEntry } from '../../../../api/workshops';

import { processAuditEntry } from './auditEventHelpers';
import { AuditSubRowLine } from './AuditSubRowLine';
import { formatAuditDate, formatAuditTime } from './workshopFields';

// ─── Main component ───────────────────────────────────────────────────────────

interface Props {
  entry: AuditEntry;
}

export function AuditEventGroup({ entry }: Props) {
  const data = processAuditEntry(entry);

  return (
    <div
      data-testid="audit-event-group"
      style={{ padding: '8px 0', borderBottom: '1px solid var(--nb-border)' }}
    >
      {/* Title line: bold title left + actor·date·clock right */}
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--nb-ink)' }}>{data.title}</span>
        <span
          style={{
            flexShrink: 0,
            fontSize: 10,
            color: 'var(--nb-subtle)',
            whiteSpace: 'nowrap',
          }}
        >
          {data.actorName} · {formatAuditDate(data.changedAt)} · {formatAuditTime(data.changedAt)}
        </span>
      </div>

      {data.summary && (
        <div style={{ fontSize: 12, color: 'var(--nb-ink-2)', marginTop: 2 }}>{data.summary}</div>
      )}

      {/* Sub-rows */}
      {data.subRows.map((row) => (
        <AuditSubRowLine key={row.key} row={row} />
      ))}
    </div>
  );
}
